import React from 'react';
import { useSelector } from 'react-redux';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ScoreOutlined from '@mui/icons-material/ScoreOutlined';
import TooltipForButton from 'components/mui/Tooltip/TooltipForButton';
import CONST from 'domain/documents/constants';
import { brainCategorizationEnabledSelector } from 'domain/documents';
import { useDocumentSubtypes } from 'domain/documents/queries';

type Props = {
  // Both modes ultimately write a change-type tag: a sub-type id (brain) or base _T_FIN (legacy).
  onChangeType: (type: string) => void;
  // Legacy (brain-off) disable: every selected doc is already financial.
  disabledWhenNoSubtype: boolean;
};

const MARK_AS_FINANCIAL_TOOLTIP = {
  id: 'selectedPanel.generateJournalEntry',
  defaultMessage: 'Mark as financial document',
};

const BulkMarkAsFinancialMenu = ({ onChangeType, disabledWhenNoSubtype }: Props) => {
  const brainCategorizationEnabled = useSelector(brainCategorizationEnabledSelector);
  const popupState = usePopupState({ variant: 'popover', popupId: 'BulkMarkAsFinancial' });
  const { data: subtypes = [], isLoading } = useDocumentSubtypes();

  // brain OFF → pre-feature plain "mark as financial" (writes base _T_FIN, no sub-type).
  if (!brainCategorizationEnabled) {
    return (
      <TooltipForButton t={MARK_AS_FINANCIAL_TOOLTIP} placement="top">
        <IconButton
          aria-label="convertToFinancial"
          color="primary"
          disabled={disabledWhenNoSubtype}
          onClick={() => onChangeType(CONST.tags.financial)}
        >
          <ScoreOutlined />
        </IconButton>
      </TooltipForButton>
    );
  }

  // brain ON → sub-type menu (all items enabled; mixed current sub-types in a bulk selection).
  const handleSelect = (subtypeId: string) => () => {
    popupState.close();
    onChangeType(subtypeId);
  };

  return (
    <>
      <TooltipForButton t={MARK_AS_FINANCIAL_TOOLTIP} placement="top">
        <IconButton
          aria-label="convertToFinancial"
          color="primary"
          disabled={isLoading || subtypes.length === 0}
          {...bindTrigger(popupState)}
        >
          <ScoreOutlined />
        </IconButton>
      </TooltipForButton>
      <Menu {...bindMenu(popupState)}>
        {subtypes.map((s) => (
          <MenuItem key={s.id} onClick={handleSelect(s.id)}>
            {s.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default BulkMarkAsFinancialMenu;
